import {
  BookmarkFlag,
  type FoodMeta,
  type FoodRank,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { forwardRef, useState } from 'react';
import { cn } from '@/utils/styles';
import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import { getFoodImageUrl } from '@utils/nutrition/getImageUrl';
import { StatusIndicator } from '@design-system/StatusIndicator';
import { Card, CardContent, CardTitle } from '@design-system/Card';
import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { getFoodDetailsPageUrl } from '@/utils/nutrition/getFoodDetailsPageUrl';
import { useSaveFoodMutation } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import { EventTracker } from '@/utils';

export interface MyFoodsCardProps extends React.ComponentProps<'div'> {
  food: FoodMeta;
  variant?: 'default' | 'flat';
  rankMap?: Record<number, FoodRank>;
}

const getRankDetails = (rank?: number, rankMap?: Record<number, FoodRank>) => {
  if (!rankMap || typeof rank !== 'number') return;

  return rankMap[rank];
};

const handleFoodDetailRedirection = (path: string, title: string) => {
  EventTracker.trackEvent(
    'nutrition',
    `nutrition_myFoods_${title.replace(/\s+/g, '')}`,
  );
  return path;
};

export const MyFoodsCard = forwardRef<HTMLDivElement, MyFoodsCardProps>(
  ({ food, rankMap, className, variant = 'default', ...rest }, ref) => {
    const foodDetailsPath = getFoodDetailsPageUrl(food.id);
    const [isBookmarked, setIsBookmarked] = useState(food.is_saved);

    const handleOnSaveMutationSuccess = () => {
      setIsBookmarked((previousValue) => !previousValue);
    };

    const { mutate, isPending } = useSaveFoodMutation({
      onSuccess: handleOnSaveMutationSuccess,
    });

    const rankDetails = getRankDetails(food.rank, rankMap);

    const handleOnBookmark: React.MouseEventHandler<HTMLButtonElement> = (
      event,
    ) => {
      EventTracker.trackEvent(
        'nutrition',
        `nutrition_myFoods_${food.title.replace(/\s+/g, '')}_saved`,
      );
      event.stopPropagation();

      const bookmarkFlag = isBookmarked
        ? BookmarkFlag.Remove
        : BookmarkFlag.Add;

      mutate({
        food_id: food.id,
        flag: bookmarkFlag,
      });
    };

    return (
      <div ref={ref} className={cn('relative', className)}>
        <Link
          variant="flat"
          to={handleFoodDetailRedirection(foodDetailsPath, food.title)}
          state={{
            isBookmarked: isBookmarked,
          }}
        >
          <Card className="p-5 h-full" {...rest}>
            <CardTitle
              data-variant={variant}
              className="flex flex-row gap-4 items-center data-[variant='default']:items-start"
            >
              <div
                data-variant={variant}
                className='data-[variant="default"]:hidden min-w-16'
              >
                {food.image_url && (
                  <Image
                    width={64}
                    height={64}
                    src={getFoodImageUrl(food.image_url)}
                    className="size-16 object-contain"
                  />
                )}
              </div>

              <div className="flex flex-col gap-3 overflow-hidden w-full pr-10">
                <span className="typography-body !font-medium text-primary truncate">
                  <abbr className="no-underline" title={food.title}>
                    {food.title}
                  </abbr>
                </span>

                {rankDetails && (
                  <span className="typography-caption1 text-tertiary flex flex-row gap-2 items-center">
                    <StatusIndicator size="sm" color={rankDetails.color} />
                    {rankDetails.title}
                  </span>
                )}
              </div>
            </CardTitle>

            {food.image_url && (
              <CardContent
                data-variant={variant}
                className="data-[variant='flat']:hidden flex items-center justify-center"
              >
                <Image
                  width={128}
                  height={128}
                  src={getFoodImageUrl(food.image_url)}
                  className='data-[variant="flat"]:hidden size-32 object-contain'
                />
              </CardContent>
            )}
          </Card>
        </Link>

        <BookmarkButton
          size="icon"
          variant="ghost"
          disabled={isPending}
          isPending={isPending}
          data-variant={variant}
          bookmarked={isBookmarked}
          onClick={handleOnBookmark}
          className="absolute right-3 p-2 top-5 data-[variant=flat]:top-1/2 data-[variant=flat]:-translate-y-1/2 [&_svg]:size-6"
        />
      </div>
    );
  },
);
